import React, { useEffect } from 'react';
import { TopBar } from '../components/TopBar';
import { MainTabs } from '../components/MainTabs';
import { BottomNav } from '../components/BottomNav';
import { TrendingUp, Users, Home, MessageCircle, DollarSign, Calendar, ArrowUpRight, ArrowDownRight } from 'lucide-react';
interface DashboardPageProps {
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onPropertiesClick?: () => void;
  onClientsClick?: () => void;
  onCalendarClick?: () => void;
  onChatClick?: () => void;
  onNotificationsClick?: () => void;
  onProfileClick?: () => void;
}
export function DashboardPage({
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onPropertiesClick,
  onClientsClick,
  onCalendarClick,
  onChatClick,
  onNotificationsClick,
  onProfileClick
}: DashboardPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleTabChange = (tab: 'home' | 'clients' | 'properties') => {
    if (tab === 'clients' && onClientsClick) {
      onClientsClick();
    } else if (tab === 'properties' && onPropertiesClick) {
      onPropertiesClick();
    }
    // 'home' tab is already active, no action needed
  };
  const stats = [{
    label: 'Total Clients',
    value: '24',
    change: '+12%',
    trend: 'up' as const,
    icon: Users,
    color: 'from-blue-500 to-blue-600'
  }, {
    label: 'Active Deals',
    value: '8',
    change: '+3',
    trend: 'up' as const,
    icon: Home,
    color: 'from-green-500 to-green-600'
  }, {
    label: 'Total Revenue',
    value: '125K',
    change: '+18%',
    trend: 'up' as const,
    icon: DollarSign,
    color: 'from-purple-500 to-purple-600'
  }, {
    label: 'RMI Requests',
    value: '15',
    change: '+5',
    trend: 'up' as const,
    icon: MessageCircle,
    color: 'from-orange-500 to-orange-600'
  }];
  const recentActivity = [{
    id: 1,
    title: 'New client added',
    description: 'Ahmed Hamdy Mohamed',
    time: '2 hours ago',
    type: 'client'
  }, {
    id: 2,
    title: 'RMI request sent',
    description: 'Madinety - 2BR Apartment',
    time: '5 hours ago',
    type: 'rmi'
  }, {
    id: 3,
    title: 'Deal confirmed',
    description: 'Sarah Ahmed - Villa in Hyde Park',
    time: '1 day ago',
    type: 'deal'
  }, {
    id: 4,
    title: 'Meeting scheduled',
    description: 'Site visit with Karim Mahmoud',
    time: '2 days ago',
    type: 'meeting'
  }];
  const upcomingTasks = [{
    id: 1,
    title: 'Follow up with Ahmed',
    time: 'Today, 3:00 PM',
    priority: 'high'
  }, {
    id: 2,
    title: 'Send property options to Sarah',
    time: 'Today, 5:00 PM',
    priority: 'medium'
  }, {
    id: 3,
    title: 'Site visit - Madinety',
    time: 'Tomorrow, 10:00 AM',
    priority: 'high'
  }];
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-1056">
      <div className="w-full mx-auto bg-white min-h-screen shadow-2xl relative" data-id="element-1057">
        <TopBar onCalendarClick={onCalendarClick} onChatClick={onChatClick} data-id="element-1058" />
        <MainTabs activeTab="home" onTabChange={handleTabChange} data-id="element-1059" />

        <div className="px-4 py-6 space-y-6" data-id="element-1060">
          {/* Welcome Section */}
          <div className="bg-gradient-to-r from-secondary to-secondary-dark rounded-2xl p-6 text-white shadow-lg" data-id="element-1061">
            <h1 className="text-2xl font-bold mb-2" data-id="element-1062">Welcome back! 👋</h1>
            <p className="text-white/90 text-sm" data-id="element-1063">
              Here's what's happening with your business today
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4" data-id="element-1064">
            {stats.map((stat, index) => <div key={index} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm" data-id="element-1065">
                <div className={`w-10 h-10 bg-gradient-to-r ${stat.color} rounded-lg flex items-center justify-center mb-3`} data-id="element-1066">
                  <stat.icon className="w-5 h-5 text-white" data-id="element-1067" />
                </div>
                <p className="text-2xl font-bold text-neutral-text mb-1" data-id="element-1068">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-500 mb-2" data-id="element-1069">{stat.label}</p>
                <div className="flex items-center gap-1" data-id="element-1070">
                  {stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3 text-green-500" data-id="element-1071" /> : <ArrowDownRight className="w-3 h-3 text-red-500" data-id="element-1072" />}
                  <span className={`text-xs font-semibold ${stat.trend === 'up' ? 'text-green-500' : 'text-red-500'}`} data-id="element-1073">
                    {stat.change}
                  </span>
                </div>
              </div>)}
          </div>

          {/* Quick Actions */}
          <div data-id="element-1074">
            <h2 className="text-lg font-bold text-neutral-text mb-3" data-id="element-1075">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-1076">
              <button onClick={onClientsClick} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-secondary transition-colors text-left" data-id="element-1077">
                <Users className="w-6 h-6 text-secondary mb-2" data-id="element-1078" />
                <p className="font-semibold text-neutral-text text-sm" data-id="element-1079">
                  View Clients
                </p>
              </button>
              <button onClick={onInventoryClick} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-secondary transition-colors text-left" data-id="element-1080">
                <Home className="w-6 h-6 text-secondary mb-2" data-id="element-1081" />
                <p className="font-semibold text-neutral-text text-sm" data-id="element-1082">
                  Browse Inventory
                </p>
              </button>
              <button onClick={onRMIClick} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-secondary transition-colors text-left" data-id="element-1083">
                <MessageCircle className="w-6 h-6 text-secondary mb-2" data-id="element-1084" />
                <p className="font-semibold text-neutral-text text-sm" data-id="element-1085">
                  RMI Requests
                </p>
              </button>
              <button onClick={onCalendarClick} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-secondary transition-colors text-left" data-id="element-1086">
                <Calendar className="w-6 h-6 text-secondary mb-2" data-id="element-1087" />
                <p className="font-semibold text-neutral-text text-sm" data-id="element-1088">
                  My Calendar
                </p>
              </button>
            </div>
          </div>

          {/* Upcoming Tasks */}
          <div data-id="element-1089">
            <h2 className="text-lg font-bold text-neutral-text mb-3" data-id="element-1090">
              Upcoming Tasks
            </h2>
            <div className="space-y-3" data-id="element-1091">
              {upcomingTasks.map(task => <div key={task.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex items-start gap-3" data-id="element-1092">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${task.priority === 'high' ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-1093" />
                  <div className="flex-1" data-id="element-1094">
                    <p className="font-semibold text-neutral-text text-sm mb-1" data-id="element-1095">
                      {task.title}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1096">{task.time}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Recent Activity */}
          <div data-id="element-1097">
            <h2 className="text-lg font-bold text-neutral-text mb-3" data-id="element-1098">
              Recent Activity
            </h2>
            <div className="space-y-3" data-id="element-1099">
              {recentActivity.map(activity => <div key={activity.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm" data-id="element-1100">
                  <p className="font-semibold text-neutral-text text-sm mb-1" data-id="element-1101">
                    {activity.title}
                  </p>
                  <p className="text-xs text-gray-600 mb-1" data-id="element-1102">
                    {activity.description}
                  </p>
                  <p className="text-xs text-gray-400" data-id="element-1103">{activity.time}</p>
                </div>)}
            </div>
          </div>
        </div>

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onNotificationsClick={onNotificationsClick} onProfileClick={onProfileClick} data-id="element-1104" />
      </div>
    </div>;
}